import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { Badge, FkLink, ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, DateInput, NumberInput, Select, TextInput } from "../../components/forms/Inputs"
import { useApi } from "../../hooks/useApi"
import { useTranslations } from "../../layers/Localization"
import { NotesDeCreditDetailList } from "./NotesDeCreditDetail"
import { useOptions } from "../../hooks/useOptions"
import { TvaInput, useRegimeTvaOptions } from "./Clients"
import { Currency } from "../../hooks/helpers"
import { getTotalFacture, SendSingleEmailPopup, useStatusEnvoiEmailOptions } from "./Factures"
import { getTotalsFactureDetail } from "./FacturesDetail"
import { useToday } from "../../hooks/useToday"
import { useLangueOptions } from "./Gerants"
import { useCodesTvaOptions } from "./Articles"

const useActions = (setNoteDeCredit, setPopup, setReload) => {
    const t = useTranslations()
    return {
        [t('Aperçu note de crédit')]: x => window.open(`${process.env.REACT_APP_API_URL}/notesDeCredit/${x.id}/pdf`, '_blank').focus(),
        [t('Envoyer email')]: (x, reload) => {
            setNoteDeCredit(x)
            setPopup(true)
            setReload(() => reload)
        },
    }
}

function NotesDeCreditFields({ item, originalItem, set }) {
    const [details, setDetails] = useState([])
    const codesTvaOptions = useCodesTvaOptions()
    const t = useTranslations()
    const clientOptions = useOptions('clients', 'referenceCourte')
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()
    const api = useApi()
    const { id } = useParams()
    const langueOptions = useLangueOptions()
    const regimeTvaOptions = useRegimeTvaOptions()

    useEffect(() => {
        if (id === 'new') api.get(`/notesDeCredit?limit=1&orderBy:desc=num`).then(notesDeCredit => set('num')((notesDeCredit[0]?.num || 0) + 1))
        else {
            api.get(`/notesDeCreditDetail?notesDeCredit=${encodeURIComponent(id)}&limit=100`).then(setDetails)
        }
    }, [id])

    const onDateChange = value => {
        set('date')(value)
        set('exercice')(value.getUTCFullYear())
    }
    const onClientChange = (value, item) => {
        set('clientId')(value)
        set('clientNum')(item.num)
        set('clientNom')(item.nom)
        set('clientNumTva')(item.numTva)
        set('clientAdresse')(item.adresse)
        set('clientCp')(item.cp)
        set('clientCommune')(item.commune)
        set('clientRegimeTva')(item.regimeTva)
        set('clientLangue')(item.langue)
    }

    useEffect(() => {
        if (item.date && !item.exercice) set('exercice')(item.date.getUTCFullYear())
    }, [id])

    return <>
        <Row>
            <Select label={t("Email")} value={item.statusEnvoiEmail} onChange={set('statusEnvoiEmail')} options={statusEnvoiEmailOptions} required />
            <Checkbox center label={t("Encodée en comptabilité")} value={item.comptabilite} onChange={set('comptabilite')} />
        </Row>
        <hr />
        <Row>
            <NumberInput label={t("Numéro")} value={item.num} onChange={set('num')} required max="999999" disabled />
            <NumberInput label={t("Exercice")} value={item.exercice} onChange={set('exercice')} required disabled />
            <DateInput label={t("Date")} value={item.date} onChange={onDateChange} required disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <hr />
        <Row>
            <AsyncSelect className="grow" label={t("Client")} value={item.clientId} onChange={onClientChange} options={clientOptions} required disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput className="grow" label={t("Nom")} value={item.clientNom} onChange={set('clientNom')} required disabled={originalItem.comptabilite || item.comptabilite} />
            <Select label={t("Langue")} value={item.clientLangue} onChange={set('clientLangue')} options={langueOptions} required disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <Row>
            <TvaInput label={t("Numéro de TVA")} value={item.clientNumTva} onChange={set('clientNumTva')} disabled={originalItem.comptabilite || item.comptabilite} />
            <Select label={t("Régime TVA")} value={item.clientRegimeTva} onChange={set('clientRegimeTva')} required options={regimeTvaOptions} disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        <Row>
            <TextInput className="grow" label={t("Adresse")} value={item.clientAdresse} onChange={set('clientAdresse')} disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput label={t("Code postal")} value={item.clientCp} onChange={set('clientCp')} disabled={originalItem.comptabilite || item.comptabilite} />
            <TextInput label={t("Commune")} value={item.clientCommune} onChange={set('clientCommune')} disabled={originalItem.comptabilite || item.comptabilite} />
        </Row>
        {(!!details.length) && <div className="flex">
            <div className={`grid grid-cols-[repeat(6,auto)] gap-x-8 gap-y-3 mt-5 whitespace-nowrap overflow-auto ${item.comptabilite ? 'opacity-50' : ''}`}>
                <div></div>
                <div className="font-bold">0%</div>
                <div className="font-bold">6%</div>
                <div className="font-bold">21%</div>
                <div className="font-bold">20 bis</div>
                <div className="font-bold">{t("Totaux:")}</div>
                <div className="font-bold">{t("Bases:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalHtva, 0)} />
                <div className="font-bold">{t("TVA:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTva, 0)} />
                <div className="font-bold">{t("Total:")}</div>
                <Currency value={details.filter(x => x.codeTva === 'ZERO').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'SIX').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_ET_UN').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.filter(x => x.codeTva === 'VINGT_BIS').reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
                <Currency value={details.reduce((total, x) => total + getTotalsFactureDetail(x, codesTvaOptions).totalTtc, 0)} />
            </div>
        </div>}
    </>
}

export function NotesDeCreditList(props) {
    const codesTvaOptions = useCodesTvaOptions()
    const [noteDeCredit, setNoteDeCredit] = useState()
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [reload, setReload] = useState(false)
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()
    const t = useTranslations()
    const actions = useActions(setNoteDeCredit, setSingleEmailPopup, setReload)
    const api = useApi()

    return <>
        <ListPage model="notesDeCredit" title={t("Notes de crédit")} columns={{
            [t('Exercice')]: 'exercice',
            [t('Numéro')]: 'num',
            [t('Total TTC')]: { value: item => <Currency value={getTotalFacture(item, codesTvaOptions, 'notesDeCreditDetail').totalTtc} /> },
            [t('Date')]: 'date',
            [t('Client')]: { sort: 'client', value: item => <FkLink>{item.client?.referenceCourte}</FkLink> },
            [t('Email')]: { sort: 'statusEnvoiEmail', value: item => <Badge value={item?.statusEnvoiEmail} options={statusEnvoiEmailOptions} /> },
            [t('Encodée')]: 'comptabilite',
        }} sort={{
            [t('Exercice')]: 'desc',
            [t('Numéro')]: 'desc',
        }} join={{
            client: 'referenceCourte',
            notesDeCreditDetail: '',
        }} filter={{
            [t('Notes de crédit à envoyer')]: 'statusEnvoiEmail:not=ENVOYE',
            [t('Notes de crédit à encoder')]: 'comptabilite=false',
            [t('Notes de crédit complétées')]: `statusEnvoiEmail=ENVOYE&comptabilite=true`,
        }} actions={actions} mainActions={{
            [t('Exporter vers popsy')]: reload => {
                window.open(`${process.env.REACT_APP_API_URL}/notesDeCredit/export-popsy`)
                setTimeout(reload, 100)
            },
            [t('Tout marquer comme envoyées')]: reload => api.post(`/notesDeCredit/mark-as-sent`).then(reload),
            [t('Tout marquer comme encodées')]: reload => api.post(`/notesDeCredit/mark-as-encoded`).then(reload),
        }} {...props} />
        <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="notesDeCredit" item={noteDeCredit} reload={reload} />
    </>
}

export function NotesDeCreditDetail(props) {
    const [noteDeCredit, setNoteDeCredit] = useState()
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [reload, setReload] = useState(false)
    const t = useTranslations()
    const actions = useActions(setNoteDeCredit, setSingleEmailPopup, setReload)
    const today = useToday()

    return <>
        <DetailPage model="notesDeCredit" title={t("Note de crédit")} Fields={NotesDeCreditFields} actions={actions} nextKey="num" defaults={{
            statusEnvoiEmail: "NON_ENVOYE",
            date: new Date(today),
        }} tables={{
            [t("Détail")]: <NotesDeCreditDetailList relation="notesDeCreditId" root="." />,
        }} {...props} />
        <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="notesDeCredit" item={noteDeCredit} reload={reload} />
    </>
}